import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import CustomMultiSelect from "../../CustomMultiSelect";
import Loader from "../../loaders/Loader";
import MessageSendingData from "../MessageSendingData";
import {getAnOfferCountries} from "../../../helpers/get-an-offers-countries";
import {AUTH_TOKEN} from "../../../constants/Auth";
import {logOut} from "../../../redux/dataUploader/dataSlice";

const getCountryFilterValues = (countryFilter) => {
    if (!countryFilter?.value) return [];

    const values = Array.isArray(countryFilter.value) ? countryFilter.value : [countryFilter.value];

    return values
        .flatMap(value => String(value).split(","))
        .map(value => value.trim().replaceAll("%", "").toUpperCase())
        .filter(Boolean);
};

const filterCountriesByOfferCountry = (countries, countryFilter) => {
    if (!countryFilter?.operator) return countries;

    const filteredValues = getCountryFilterValues(countryFilter);

    if (filteredValues.length === 0) return countries;

    if (["like", "in_json", "="].includes(countryFilter.operator)) {
        return countries.filter(country => filteredValues.includes(country.value));
    }

    if (["not like", "not_json", "!="].includes(countryFilter.operator)) {
        return countries.filter(country => !filteredValues.includes(country.value));
    }

    return countries;
};

const normalizeCountryCodes = (countries) => (
    [...new Set(countries.map(country => String(country).trim().toUpperCase()).filter(Boolean))]
);

const getApiErrorMessage = (data, fallback = "Something went wrong") => {
    const rawErrors = data?.errors || data?.error;

    if (typeof rawErrors === "string") return rawErrors;

    if (Array.isArray(rawErrors)) {
        return rawErrors.map(String).map(error => error.trim()).filter(Boolean);
    }

    if (rawErrors && typeof rawErrors === "object") {
        const errors = Object.values(rawErrors)
            .flat()
            .map(String)
            .map(error => error.trim())
            .filter(Boolean);

        if (errors.length > 0) return errors;
    }

    return data?.message || fallback;
};

const CSVinBbFormatPopup = ({
                                closePopup,
                                serviceId,
                                userId,
                                urlSearchParams,
                                filterParams,
                                where,
                                offerSortColumn,
                                offerSortDirection,
                            }) => {
    const dispatch = useDispatch();
    const [countriesList, setCountriesList] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [includeAllAccounts, setIncludeAllAccounts] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmationPopupText, setConfirmationPopupText] = useState("");
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);

    useEffect(() => {
        const loadCountries = async () => {
            const anCountries = await getAnOfferCountries();
            const activeCountryFilter = filterParams?.offer_country || urlSearchParams?.offer_country;
            const countries = Object.entries(anCountries || {}).map(([value, label]) => ({
                value: String(value).toUpperCase(),
                label,
            }));

            setCountriesList(filterCountriesByOfferCountry(countries, activeCountryFilter));

            if (["like", "in_json", "="].includes(activeCountryFilter?.operator)) {
                setSelectedCountries(getCountryFilterValues(activeCountryFilter));
            }
        };

        loadCountries();
    }, [filterParams, urlSearchParams]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        const requestBody = {
            email: localStorage.getItem("email"),
            countries: normalizeCountryCodes(selectedCountries),
            where,
            offerSortColumn,
            offerSortDirection,
            include_all_accounts: includeAllAccounts,
        };

        try {
            const response = await fetch(
                `${localStorage.getItem("apiUrl")}api/affiliate-network/download-offers-bb-format/${serviceId}/${userId}`,
                {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`,
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (response.status === 401) dispatch(logOut());

            let data = {};
            try {
                data = await response.json();
            } catch (error) {
                data = {};
            }

            setConfirmationPopupText(response.ok
                ? data.message || "After the download is complete, the offers will be sent to your mail."
                : getApiErrorMessage(data));
            setShowConfirmationPopup(true);
        } catch (error) {
            setConfirmationPopupText(error.message || "Something went wrong");
            setShowConfirmationPopup(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="add-item-popup-container voluum-popup-container">
            <div className="add-item-popup-block">
                <div className="form-wrap">
                    <div className="title-wrap">
                        <h1>Download CSV - BB Format</h1>
                        <div className="close-btn" onClick={closePopup}>
                            <img
                                src={`${process.env.PUBLIC_URL}/images/close-btn.svg`}
                                alt="close popup"
                            />
                        </div>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="form-flex-container">
                            <div className="input-block without-config-item-block input-search-select-block">
                                <label>Choose one or more countries (all countries selected by default)</label>
                                <CustomMultiSelect
                                    key={`${countriesList.length}-${selectedCountries.join(",")}`}
                                    options={countriesList}
                                    defaultValue={countriesList.filter(country => selectedCountries.includes(country.value))}
                                    onChange={values => setSelectedCountries(normalizeCountryCodes(values))}
                                />
                            </div>

                            <div className="input-block config-item-block checkbox-block">
                                <input
                                    id="bb-include-all-accounts"
                                    type="checkbox"
                                    className="toggle-button"
                                    checked={includeAllAccounts}
                                    onChange={event => setIncludeAllAccounts(event.target.checked)}
                                />
                                <label htmlFor="bb-include-all-accounts" className="toggle-label">
                                    Include all AN accounts
                                </label>
                            </div>

                            <div className="input-block without-config-item-block after-checkbox-block">
                                {loading ? (
                                    <Loader />
                                ) : (
                                    <button type="submit" className="blue-btn">
                                        Download CSV - BB format
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>

            {showConfirmationPopup && (
                <MessageSendingData
                    messageText={confirmationPopupText}
                    setClosePopup={() => setShowConfirmationPopup(false)}
                />
            )}
        </div>
    );
};

export default CSVinBbFormatPopup;
